import { useState, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router';
import { useUserContext } from '../context/UserContext';
import { useComplaint, useAddComment, useMediaUpload, useCategories, useManufacturers } from '../hooks';
import { HistoryTimeline, StatusUpdateModal } from '../components/complaints';
import type { ComplaintStatus } from '../api';
import { updateClaimValue, updateComplaintFields } from '../api';
import { isAdmin } from '../lib/roles';
import { isIOSDevice } from '../utils/platform';
import { downloadPdf, deliverPdfIOS } from '../utils/deliverPdf';
import {
  ArrowLeft,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Store,
  Building2,
  Package,
  Calendar,
  User,
  Send,
  Loader2,
  Image as ImageIcon,
  Hash,
  Paperclip,
  X,
  FileText,
  ExternalLink,
  Factory,
  CalendarDays,
  Boxes,
  DollarSign,
  Pencil,
  FileDown,
} from 'lucide-react';
import { generateComplaintPdf } from '@/utils/generateComplaintPdf';

const statusConfig: Record<
  ComplaintStatus,
  { label: string; bg: string; text: string; icon: React.ReactNode }
> = {
  submitted: {
    label: 'Submitted',
    bg: 'bg-indigo-500',
    text: 'text-white',
    icon: <Clock className="w-4 h-4" />,
  },
  under_review: {
    label: 'Under Review',
    bg: 'bg-yellow-500',
    text: 'text-white',
    icon: <AlertCircle className="w-4 h-4" />,
  },
  approved: {
    label: 'Approved',
    bg: 'bg-green-500',
    text: 'text-white',
    icon: <CheckCircle2 className="w-4 h-4" />,
  },
  rejected: {
    label: 'Rejected',
    bg: 'bg-red-500',
    text: 'text-white',
    icon: <XCircle className="w-4 h-4" />,
  },
};

function StatusBadge({ status }: { status: ComplaintStatus }) {
  const config = statusConfig[status];
  return (
    <span
      data-testid="complaint-badge-status"
      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${config.bg} ${config.text}`}
    >
      {config.icon}
      {config.label}
    </span>
  );
}

function formatDate(timestamp: number): string {
  return new Date(timestamp).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatCategory(category: string): string {
  return category
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function isImageFile(key: string): boolean {
  return /\.(jpg|jpeg|png|gif|webp)$/i.test(key);
}

function isPdfFile(key: string): boolean {
  return /\.pdf$/i.test(key);
}

function getFileName(key: string): string {
  const parts = key.split('/');
  return parts[parts.length - 1];
}

// Info row component for cleaner layout
function InfoRow({
  icon: Icon,
  label,
  value,
  subValue,
  mono,
  testId,
}: {
  icon: React.ElementType;
  label: string;
  value: string;
  subValue?: string;
  mono?: boolean;
  testId?: string;
}) {
  return (
    <div className="flex items-start gap-3 py-3" data-testid={testId}>
      <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
        <Icon className="w-4 h-4 text-gray-500" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-gray-500 uppercase tracking-wide">{label}</p>
        <p className={`text-sm font-medium text-gray-900 ${mono ? 'font-mono' : ''}`}>{value}</p>
        {subValue && <p className="text-xs text-gray-500 mt-0.5">{subValue}</p>}
      </div>
    </div>
  );
}

export function ComplaintDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, brandId } = useUserContext();

  const { data: categoriesData } = useCategories(!!user && !!brandId, brandId ?? undefined);
  const categoryLabels = useMemo(
    () =>
      (categoriesData ?? []).reduce<Record<string, string>>((acc, c) => {
        acc[c.categoryId] = c.label;
        return acc;
      }, {}),
    [categoriesData]
  );

  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [commentError, setCommentError] = useState<string | null>(null);
  const [editingClaimValue, setEditingClaimValue] = useState(false);
  const [claimValueInput, setClaimValueInput] = useState('');
  const [claimValueError, setClaimValueError] = useState<string | null>(null);
  const [isSavingClaimValue, setIsSavingClaimValue] = useState(false);

  // Inline admin editing state
  const [editingSection, setEditingSection] = useState<'details' | 'description' | null>(null);
  const [editFields, setEditFields] = useState<Record<string, string>>({});
  const [editError, setEditError] = useState<string | null>(null);
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data, isLoading, error, refetch } = useComplaint(id);

  // Manufacturer catalog for edit mode dropdown (filtered by complaint's distributor)
  const complaintSupplierId = data?.data?.complaint?.supplierId;
  const { data: manufacturersData } = useManufacturers(complaintSupplierId, !!user && !!brandId && !!complaintSupplierId);
  const manufacturersList = manufacturersData?.data || [];

  const addCommentMutation = useAddComment(id || '');

  // Seed dedup set with checksums of media already on the complaint
  // (bag/case + everything from prior comments). The detail endpoint
  // returns checksum on each MediaWithUrl when one was recorded at upload.
  const existingChecksums = useMemo(() => {
    const list: { checksum: string; fileName: string }[] = [];
    const media = data?.data?.complaint?.media || [];
    for (const m of media) {
      if (m.checksum) {
        list.push({ checksum: m.checksum, fileName: m.fileName || 'an existing attachment' });
      }
    }
    return list;
  }, [data?.data?.complaint?.media]);

  const {
    uploads,
    isUploading,
    uploadFiles,
    removeUpload,
    clearUploads,
    completedMediaKeys,
    completedMediaChecksums,
  } = useMediaUpload({ maxFiles: 5, existingChecksums });

  const isVendor = user?.userType === 'vendor';
  const isUserAdmin = isAdmin(user?.roles);
  const canUpdateStatus = (isVendor || isUserAdmin) && data?.data?.complaint;
  const canEditClaimValue = isVendor || isUserAdmin;

  const handleStartEdit = (section: 'details' | 'description') => {
    if (!data?.data?.complaint) return;
    const c = data.data.complaint;
    if (section === 'details') {
      setEditFields({
        manufacturer: c.manufacturer || '',
        manufacturerId: c.manufacturerId || '',
        category: c.category || '',
        batchCode: c.batchCode || '',
        affectedUnits: c.affectedUnits ? String(c.affectedUnits) : '',
        affectedUnitType: c.affectedUnitType || 'BAG',
        deliveryDate: c.deliveryDate
          ? new Date(c.deliveryDate).toISOString().split('T')[0]
          : '',
      });
    } else {
      setEditFields({ description: c.description });
    }
    setEditingSection(section);
    setEditError(null);
  };

  const handleSaveEdit = async () => {
    if (!data?.data?.complaint) return;
    const c = data.data.complaint;
    setIsSavingEdit(true);
    setEditError(null);
    try {
      const payload: Record<string, unknown> = { updatedAt: c.updatedAt };
      if (editingSection === 'details') {
        if (editFields.manufacturerId && editFields.manufacturerId !== c.manufacturerId) {
          payload.manufacturerId = editFields.manufacturerId;
        } else if (editFields.manufacturer && editFields.manufacturer !== c.manufacturer) {
          payload.manufacturer = editFields.manufacturer.trim();
        }
        if (editFields.category && editFields.category !== c.category) payload.category = editFields.category;
        if (editFields.batchCode) payload.batchCode = editFields.batchCode.trim();
        if (editFields.affectedUnits)
          payload.affectedUnits = parseInt(editFields.affectedUnits, 10);
        if (editFields.affectedUnitType) payload.affectedUnitType = editFields.affectedUnitType;
        if (editFields.deliveryDate)
          payload.deliveryDate = new Date(editFields.deliveryDate).getTime();
      } else {
        payload.description = editFields.description;
      }
      await updateComplaintFields(
        c.complaintId,
        payload as Record<string, unknown> & { updatedAt: number }
      );
      await refetch();
      setEditingSection(null);
    } catch (err) {
      if (
        err instanceof Error &&
        (err.message.includes('409') ||
          err.message.includes('conflict') ||
          err.message.includes('modified'))
      ) {
        setEditError(
          'This complaint was modified by someone else. Please refresh and try again.'
        );
      } else {
        setEditError(err instanceof Error ? err.message : 'Failed to save changes');
      }
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingSection(null);
    setEditError(null);
  };

  const handleDownloadPdf = async () => {
    if (!data?.data) return;
    setIsGeneratingPdf(true);
    try {
      const { complaint: c } = data.data;
      const catLabel = categoryLabels[c.category] ?? c.categoryLabel;
      const { blob, filename } = await generateComplaintPdf(c, catLabel);

      if (isIOSDevice()) {
        // iOS WebKit ignores <a download>, and WKWebView (HutBot) silently
        // blocks top-level blob: navigation AND window.open after async work.
        // Web Share API is the only path that reliably gives the user a way
        // to save the PDF (one tap → iOS share sheet → "Save to Files").
        // See LEARNINGS.md for the other approaches we tried.
        await deliverPdfIOS(blob, filename);
      } else {
        downloadPdf(blob, filename);
      }
    } catch (err) {
      // User dismissed the iOS share sheet — not an error.
      if (err instanceof DOMException && err.name === 'AbortError') {
        return;
      }
      console.error('PDF generation failed:', err);
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const handleSaveClaimValue = async () => {
    const parsed = parseFloat(claimValueInput);
    if (isNaN(parsed) || parsed < 0) {
      setClaimValueError('Please enter a valid positive number');
      return;
    }
    if (parsed > 1000000) {
      setClaimValueError('Value cannot exceed 1,000,000');
      return;
    }
    // Max 2 decimal places
    if (claimValueInput.includes('.') && claimValueInput.split('.')[1].length > 2) {
      setClaimValueError('Maximum 2 decimal places allowed');
      return;
    }
    setIsSavingClaimValue(true);
    setClaimValueError(null);
    try {
      await updateClaimValue(data!.data!.complaint.complaintId, parsed);
      await refetch();
      setEditingClaimValue(false);
    } catch (err) {
      setClaimValueError(err instanceof Error ? err.message : 'Failed to save claim value');
    } finally {
      setIsSavingClaimValue(false);
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    try {
      await uploadFiles(Array.from(files), id);
    } catch (err) {
      setCommentError(err instanceof Error ? err.message : 'Failed to upload files');
    }

    // Reset the input so the same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim() && completedMediaKeys.length === 0) {
      setCommentError('Please enter a comment or attach an image');
      return;
    }

    setCommentError(null);

    try {
      await addCommentMutation.mutateAsync({
        comment: newComment.trim() || '(attachment)',
        mediaUrls: completedMediaKeys.length > 0 ? completedMediaKeys : undefined,
        ...(Object.keys(completedMediaChecksums).length > 0 && {
          mediaChecksums: completedMediaChecksums,
        }),
      });
      setNewComment('');
      clearUploads();
    } catch (err) {
      setCommentError(err instanceof Error ? err.message : 'Failed to add comment');
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-4">
        <button
          onClick={() => navigate('/complaints')}
          data-testid="complaint-btn-back"
          className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Complaints
        </button>
        <div data-testid="complaint-loading" className="bg-white rounded-xl border border-gray-200 p-12 flex items-center justify-center">
          <Loader2 className="w-8 h-8 text-gray-500 animate-spin" />
        </div>
      </div>
    );
  }

  // Error state
  if (error || !data?.data) {
    return (
      <div className="space-y-4">
        <button
          onClick={() => navigate('/complaints')}
          data-testid="complaint-btn-back"
          className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Complaints
        </button>
        <div className="bg-white rounded-xl border border-red-200 p-6">
          <p className="text-red-600">
            {error instanceof Error ? error.message : 'Complaint not found'}
          </p>
        </div>
      </div>
    );
  }

  const { complaint, history } = data.data;

  return (
    <div className="space-y-4">
      {/* Back button */}
      <button
        onClick={() => navigate('/complaints')}
        data-testid="complaint-btn-back"
        className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Complaints
      </button>

      {/* Main content grid */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Left column - Complaint details */}
        <div className="lg:col-span-2 space-y-4">
          {/* Header card */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-start justify-between gap-3 mb-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <Hash className="w-4 h-4 text-primary-600 flex-shrink-0" />
                  <span data-testid="complaint-text-reference" className="text-base font-bold text-primary-600 font-mono">
                    {complaint.referenceNumber}
                  </span>
                </div>
                <h1 className="text-lg font-bold text-gray-900 truncate">{complaint.productName}</h1>
              </div>
              <StatusBadge status={complaint.status} />
            </div>

            <div className="flex gap-2">
              {canUpdateStatus && (
                <button
                  onClick={() => setIsStatusModalOpen(true)}
                  data-testid="complaint-btn-update-status"
                  className="flex-1 px-4 py-2.5 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors"
                >
                  Update Status
                </button>
              )}
              <button
                onClick={handleDownloadPdf}
                disabled={isGeneratingPdf}
                data-testid="complaint-btn-download-pdf"
                className="px-4 py-2.5 bg-white border border-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {isGeneratingPdf ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <FileDown className="w-4 h-4" />
                )}
                Generate Report (PDF)
              </button>
            </div>
          </div>

          {/* Details card */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Details</h2>
              {isUserAdmin && editingSection !== 'details' && (
                <button
                  onClick={() => handleStartEdit('details')}
                  className="p-1 text-gray-400 hover:text-gray-600 rounded"
                  title="Edit details"
                >
                  <Pencil className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            {editingSection === 'details' ? (
              <div className="space-y-3">
                {/* Non-editable fields */}
                <InfoRow
                  icon={Store}
                  label="Restaurant"
                  value={complaint.storeName}
                  subValue={
                    complaint.champsNumber
                      ? `Champs #${complaint.champsNumber} · AIS #${complaint.storeId}`
                      : `AIS #${complaint.storeId}`
                  }
                  testId="complaint-info-store"
                />
                <InfoRow icon={Building2} label="Distributor" value={complaint.supplierName} testId="complaint-info-supplier" />

                {/* Editable: Manufacturer */}
                <div>
                  <label className="text-xs text-gray-500 uppercase tracking-wide block mb-1">Manufacturer</label>
                  {manufacturersList.length > 0 ? (
                    <select
                      value={editFields.manufacturerId || ''}
                      onChange={(e) => {
                        const mfr = manufacturersList.find(m => m.manufacturerId === e.target.value);
                        setEditFields((f) => ({
                          ...f,
                          manufacturerId: e.target.value || '',
                          manufacturer: mfr?.name || f.manufacturer,
                        }));
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-300 bg-white"
                    >
                      <option value="">Select manufacturer</option>
                      {manufacturersList.map((m) => (
                        <option key={m.manufacturerId} value={m.manufacturerId}>{m.name}</option>
                      ))}
                      {/* Include current manufacturer if not in catalog (legacy free text) */}
                      {editFields.manufacturer && !manufacturersList.some(m => m.name === editFields.manufacturer) && (
                        <option value="">{editFields.manufacturer}</option>
                      )}
                    </select>
                  ) : (
                    <input
                      value={editFields.manufacturer}
                      onChange={(e) => setEditFields((f) => ({ ...f, manufacturer: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-300"
                      placeholder="Manufacturer name"
                    />
                  )}
                </div>

                {/* Editable: Category */}
                <div>
                  <label className="text-xs text-gray-500 uppercase tracking-wide block mb-1">Category</label>
                  <select
                    value={editFields.category}
                    onChange={(e) => setEditFields((f) => ({ ...f, category: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-300 bg-white"
                  >
                    <option value="">Select category</option>
                    {(categoriesData ?? []).filter(c => c.isActive).map((cat) => (
                      <option key={cat.categoryId} value={cat.categoryId}>{cat.label}</option>
                    ))}
                    {/* Include current category if inactive (historical) */}
                    {editFields.category && !(categoriesData ?? []).some(c => c.isActive && c.categoryId === editFields.category) && (
                      <option value={editFields.category}>
                        {categoryLabels[editFields.category] ?? formatCategory(editFields.category)}
                      </option>
                    )}
                  </select>
                </div>
                {complaint.productCode && (
                  <InfoRow icon={Hash} label="Product Code" value={complaint.productCode} mono testId="complaint-info-product-code" />
                )}


                {/* Editable: Delivery Date */}
                <div>
                  <label className="text-xs text-gray-500 uppercase tracking-wide block mb-1">Delivery Date</label>
                  <input
                    type="date"
                    value={editFields.deliveryDate}
                    onChange={(e) => setEditFields((f) => ({ ...f, deliveryDate: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-300"
                  />
                </div>

                {/* Editable: Batch Code */}
                <div>
                  <label className="text-xs text-gray-500 uppercase tracking-wide block mb-1">Batch Code</label>
                  <input
                    value={editFields.batchCode}
                    onChange={(e) => setEditFields((f) => ({ ...f, batchCode: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-300"
                    placeholder="Batch code"
                  />
                </div>

                {/* Editable: Affected Units */}
                <div>
                  <label className="text-xs text-gray-500 uppercase tracking-wide block mb-1">Affected Units</label>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      value={editFields.affectedUnits}
                      onChange={(e) => setEditFields((f) => ({ ...f, affectedUnits: e.target.value }))}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-300"
                      placeholder="Number of affected units"
                      min={0}
                    />
                    <select
                      value={editFields.affectedUnitType || 'BAG'}
                      onChange={(e) => setEditFields((f) => ({ ...f, affectedUnitType: e.target.value }))}
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-300 bg-white min-w-[100px]"
                    >
                      <option value="BAG">Bag(s)</option>
                      <option value="CASE">Case(s)</option>
                    </select>
                  </div>
                </div>

                {/* Error + Save/Cancel */}
                {editError && <p className="text-xs text-red-600">{editError}</p>}
                <div className="flex gap-2 pt-2">
                  <button
                    onClick={handleSaveEdit}
                    disabled={isSavingEdit}
                    className="px-3 py-1.5 bg-primary-600 text-white rounded text-sm hover:bg-primary-700 disabled:opacity-50"
                  >
                    {isSavingEdit ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    className="px-3 py-1.5 text-gray-600 hover:text-gray-800 text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
            <div className="divide-y divide-gray-100">
              <InfoRow
                icon={Store}
                label="Restaurant"
                value={complaint.storeName}
                subValue={
                  complaint.champsNumber
                    ? `Champs #${complaint.champsNumber} · AIS #${complaint.storeId}`
                    : `AIS #${complaint.storeId}`
                }
                testId="complaint-info-store"
              />
              <InfoRow icon={Building2} label="Distributor" value={complaint.supplierName} testId="complaint-info-supplier" />
              <InfoRow icon={Factory} label="Manufacturer" value={complaint.manufacturer} testId="complaint-info-manufacturer" />
              <InfoRow
                icon={Package}
                label="Category"
                value={
                  categoryLabels[complaint.category] ??
                  complaint.categoryLabel ??
                  formatCategory(complaint.category)
                }
                testId="complaint-info-category"
              />
              {complaint.productCode && (
                <InfoRow icon={Hash} label="Product Code" value={complaint.productCode} mono testId="complaint-info-product-code" />
              )}
              {complaint.deliveryDate && (
                <InfoRow icon={CalendarDays} label="Delivery Date" value={new Date(complaint.deliveryDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} testId="complaint-info-delivery-date" />
              )}
              {complaint.batchCode && (
                <InfoRow icon={Hash} label="Batch Code" value={complaint.batchCode} mono testId="complaint-info-batch-code" />
              )}
              {complaint.affectedUnits && (
                <InfoRow icon={Boxes} label="Affected Units" value={complaint.affectedUnitType ? `${complaint.affectedUnits} ${complaint.affectedUnitType === 'BAG' ? 'Bag(s)' : 'Case(s)'}` : String(complaint.affectedUnits)} testId="complaint-info-affected-units" />
              )}
              <div className="flex items-start gap-3 py-3" data-testid="complaint-info-claim-value">
                <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                  <DollarSign className="w-4 h-4 text-gray-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Value of Claim</p>
                  {editingClaimValue ? (
                    <div className="flex items-center gap-2 mt-1">
                      <input
                        type="number"
                        value={claimValueInput}
                        onChange={(e) => { setClaimValueInput(e.target.value); setClaimValueError(null); }}
                        placeholder="0.00"
                        min={0}
                        step={0.01}
                        className="w-32 px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-gray-300"
                        autoFocus
                      />
                      <button onClick={handleSaveClaimValue} disabled={isSavingClaimValue} className="px-2 py-1.5 bg-primary-600 text-white rounded text-xs font-medium hover:bg-primary-700 disabled:opacity-50">
                        {isSavingClaimValue ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Save'}
                      </button>
                      <button onClick={() => { setEditingClaimValue(false); setClaimValueError(null); }} className="px-2 py-1.5 text-gray-500 hover:text-gray-700 text-xs">Cancel</button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-gray-900">
                        {complaint.claimValue != null ? `£${Number(complaint.claimValue).toFixed(2)}` : '—'}
                      </p>
                      {canEditClaimValue && (
                        <button
                          onClick={() => { setClaimValueInput(complaint.claimValue != null ? String(complaint.claimValue) : ''); setEditingClaimValue(true); }}
                          className="p-1 text-gray-400 hover:text-gray-600 rounded"
                          title="Edit claim value"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  )}
                  {claimValueError && <p className="text-xs text-red-600 mt-1">{claimValueError}</p>}
                </div>
              </div>
              <InfoRow
                icon={Calendar}
                label="Submitted"
                value={formatDate(complaint.submittedAt)}
                subValue={complaint.submittedByName}
                testId="complaint-info-submitted"
              />
            </div>
            )}
          </div>

          {/* Description card */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Description</h2>
              {isUserAdmin && editingSection !== 'description' && (
                <button
                  onClick={() => handleStartEdit('description')}
                  className="p-1 text-gray-400 hover:text-gray-600 rounded"
                  title="Edit description"
                >
                  <Pencil className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            {editingSection === 'description' ? (
              <div className="space-y-3">
                <textarea
                  value={editFields.description}
                  onChange={(e) => setEditFields((f) => ({ ...f, description: e.target.value }))}
                  rows={5}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-300 resize-none"
                />
                {editError && <p className="text-xs text-red-600">{editError}</p>}
                <div className="flex gap-2">
                  <button
                    onClick={handleSaveEdit}
                    disabled={isSavingEdit}
                    className="px-3 py-1.5 bg-primary-600 text-white rounded text-sm hover:bg-primary-700 disabled:opacity-50"
                  >
                    {isSavingEdit ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    className="px-3 py-1.5 text-gray-600 hover:text-gray-800 text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{complaint.description}</p>
            )}
          </div>

          {/* Structured photos (Bag & Case) */}
          {(complaint.bagPictureUrl || complaint.casePictureUrl) && (
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                Required Photos
              </h2>
              <div className="grid grid-cols-2 gap-3">
                {complaint.bagPictureUrl && (() => {
                  const bagMedia = complaint.media?.find((m) => m.key === complaint.bagPictureUrl);
                  return bagMedia ? (
                    <a href={bagMedia.downloadUrl} target="_blank" rel="noopener noreferrer" className="group block">
                      <div className="relative bg-gray-100 rounded-lg overflow-hidden border border-gray-200 hover:border-gray-400 transition-colors aspect-square">
                        <img src={bagMedia.downloadUrl} alt="Bag Picture" className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center rounded-lg">
                          <ExternalLink className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 mt-1 text-center">Bag Picture</p>
                    </a>
                  ) : (
                    <div>
                      <div className="bg-gray-50 rounded-lg border border-gray-200 aspect-square flex items-center justify-center">
                        <p className="text-xs text-gray-400">Bag Picture</p>
                      </div>
                    </div>
                  );
                })()}
                {complaint.casePictureUrl && (() => {
                  const caseMedia = complaint.media?.find((m) => m.key === complaint.casePictureUrl);
                  return caseMedia ? (
                    <a href={caseMedia.downloadUrl} target="_blank" rel="noopener noreferrer" className="group block">
                      <div className="relative bg-gray-100 rounded-lg overflow-hidden border border-gray-200 hover:border-gray-400 transition-colors aspect-square">
                        <img src={caseMedia.downloadUrl} alt="Case Picture" className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center rounded-lg">
                          <ExternalLink className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 mt-1 text-center">Case Picture</p>
                    </a>
                  ) : (
                    <div>
                      <div className="bg-gray-50 rounded-lg border border-gray-200 aspect-square flex items-center justify-center">
                        <p className="text-xs text-gray-400">Case Picture</p>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          )}

          {/* Additional attachments gallery */}
          {complaint.media && complaint.media.filter((m) => m.key !== complaint.bagPictureUrl && m.key !== complaint.casePictureUrl).length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                Additional Attachments ({complaint.media.filter((m) => m.key !== complaint.bagPictureUrl && m.key !== complaint.casePictureUrl).length})
              </h2>
              <div className="flex flex-wrap gap-3">
                {complaint.media.filter((m) => m.key !== complaint.bagPictureUrl && m.key !== complaint.casePictureUrl).map((media, index) => (
                  <a
                    key={media.key}
                    href={media.downloadUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group relative block"
                  >
                    {isImageFile(media.key) ? (
                      <div className="w-24 h-24 bg-gray-100 rounded-lg overflow-hidden border border-gray-200 hover:border-gray-400 transition-colors">
                        <img
                          src={media.downloadUrl}
                          alt={`Attachment ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center rounded-lg">
                          <ExternalLink className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      </div>
                    ) : isPdfFile(media.key) ? (
                      <div className="flex items-center gap-3 px-4 py-3 bg-red-50 rounded-lg border border-red-200 hover:border-red-400 transition-colors">
                        <FileText className="w-8 h-8 text-red-600 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate max-w-[150px]">
                            {getFileName(media.key)}
                          </p>
                          <p className="text-xs text-gray-500">PDF Document</p>
                        </div>
                        <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-red-600 transition-colors flex-shrink-0" />
                      </div>
                    ) : (
                      <div className="flex items-center gap-3 px-4 py-3 bg-gray-50 rounded-lg border border-gray-200 hover:border-gray-400 transition-colors">
                        <ImageIcon className="w-8 h-8 text-gray-500 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate max-w-[150px]">
                            {getFileName(media.key)}
                          </p>
                        </div>
                        <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-gray-600 transition-colors flex-shrink-0" />
                      </div>
                    )}
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right column - Activity & Comments */}
        <div className="lg:col-span-2 min-h-0">
          <div className="bg-white rounded-xl border border-gray-200 lg:h-full flex flex-col min-h-0 max-h-[80vh] lg:max-h-none">
            {/* Activity header */}
            <div className="px-5 py-4 border-b border-gray-100">
              <h2 className="text-sm font-semibold text-gray-900">Activity</h2>
            </div>

            {/* Timeline */}
            <div data-testid="complaint-timeline" className="flex-1 p-5 overflow-y-auto">
              <HistoryTimeline history={history} />
            </div>

            {/* Comment form - fixed at bottom */}
            <div className="border-t border-gray-100 p-4">
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                  <User className="w-4 h-4 text-gray-600" />
                </div>
                <div className="flex-1">
                  <textarea
                    value={newComment}
                    onChange={(e) => {
                      setNewComment(e.target.value);
                      if (commentError) setCommentError(null);
                    }}
                    placeholder="Write a comment..."
                    rows={2}
                    data-testid="complaint-input-comment"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-300 focus:border-transparent resize-none"
                  />

                  {/* Upload previews */}
                  {uploads.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {uploads.map((upload) => (
                        <div
                          key={upload.fileId}
                          className="relative group w-16 h-16 bg-gray-100 rounded-lg border border-gray-200 overflow-hidden"
                        >
                          {upload.status === 'uploading' && (
                            <div className="absolute inset-0 flex items-center justify-center bg-white/80">
                              <div className="w-8 h-8 relative">
                                <svg className="w-8 h-8 -rotate-90">
                                  <circle
                                    cx="16"
                                    cy="16"
                                    r="12"
                                    fill="none"
                                    stroke="#e5e7eb"
                                    strokeWidth="3"
                                  />
                                  <circle
                                    cx="16"
                                    cy="16"
                                    r="12"
                                    fill="none"
                                    stroke="#DE1D3B"
                                    strokeWidth="3"
                                    strokeDasharray={`${upload.progress * 0.75} 100`}
                                  />
                                </svg>
                                <span className="absolute inset-0 flex items-center justify-center text-[10px] font-medium text-gray-600">
                                  {upload.progress}%
                                </span>
                              </div>
                            </div>
                          )}
                          {upload.status === 'completed' && (
                            <div className="w-full h-full flex items-center justify-center bg-green-50">
                              <CheckCircle2 className="w-5 h-5 text-green-500" />
                            </div>
                          )}
                          {upload.status === 'error' && (
                            <div className="w-full h-full flex items-center justify-center bg-red-50">
                              <AlertCircle className="w-5 h-5 text-red-500" />
                            </div>
                          )}
                          {upload.status === 'pending' && (
                            <div className="w-full h-full flex items-center justify-center">
                              <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
                            </div>
                          )}
                          <button
                            onClick={() => removeUpload(upload.fileId)}
                            className="absolute -top-1 -right-1 w-5 h-5 bg-gray-800 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="w-3 h-3" />
                          </button>
                          <p className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-[8px] px-1 truncate">
                            {upload.fileName}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}

                  {commentError && (
                    <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {commentError}
                    </p>
                  )}

                  <div className="flex items-center justify-between mt-2">
                    {/* File input */}
                    <div>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*,video/*,.pdf,.heic,.heif"
                        multiple
                        onChange={handleFileSelect}
                        data-testid="complaint-input-file-upload"
                        className="hidden"
                      />
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={isUploading || uploads.length >= 5}
                        data-testid="complaint-btn-attach"
                        aria-label="Attach files"
                        className="p-2.5 min-h-[44px] min-w-[44px] flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Attach files"
                      >
                        <Paperclip className="w-5 h-5" />
                      </button>
                    </div>

                    <button
                      onClick={handleAddComment}
                      disabled={
                        addCommentMutation.isPending ||
                        isUploading ||
                        (!newComment.trim() && completedMediaKeys.length === 0)
                      }
                      data-testid="complaint-btn-post"
                      className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
                    >
                      {addCommentMutation.isPending ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Sending...
                        </>
                      ) : isUploading ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4" />
                          Post
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Status update modal */}
      {canUpdateStatus && (
        <StatusUpdateModal
          isOpen={isStatusModalOpen}
          onClose={() => setIsStatusModalOpen(false)}
          complaintId={complaint.complaintId}
          currentStatus={complaint.status}
        />
      )}
    </div>
  );
}
